/**
 * Settings store — reactive Svelte 5 $state shared across the app.
 *
 * Why .svelte.ts not .ts:
 *  Svelte 5's `$state` / `$effect` runes only run through the compiler when
 *  the file uses the `.svelte`, `.svelte.ts`, or `.svelte.js` extension.
 *
 * Usage:
 *   import { settings } from '../state/settings.svelte';
 *   settings.mode = 'dark';                       // setter triggers effects + persist
 *   <input bind:group={settings.mode} />          // works because reactive
 *
 * Persistence: a $effect.root watches every field and debounces a JSON write
 * to localStorage 100ms after the last change. coerceShape() drops unknown
 * keys and snaps invalid values back to defaults, so a stale or corrupt
 * blob can't lock the user out — and legacy keys from earlier versions
 * (theme / bodyFont / measure / lineHeight / editorZoom) are silently
 * discarded on load.
 */

import { safeGetItem, safeSetItem } from '../lib/safeStorage'

export type Mode = 'auto' | 'light' | 'dark'
export type LayoutMode = 'write' | 'read'

export interface SettingsShape {
  mode: Mode
  previewZoom: number
  layoutMode: LayoutMode
}

const STORAGE_KEY = 'md.settings'
const ZOOM_MIN = 50
const ZOOM_MAX = 300

const DEFAULTS: SettingsShape = {
  mode: 'auto',
  previewZoom: 100,
  layoutMode: 'write',
}

const VALID_MODES: ReadonlySet<Mode> = new Set(['auto', 'light', 'dark'])
const VALID_LAYOUT_MODES: ReadonlySet<LayoutMode> = new Set(['write', 'read'])

function clampZoom(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return DEFAULTS.previewZoom
  if (raw < ZOOM_MIN) return ZOOM_MIN
  if (raw > ZOOM_MAX) return ZOOM_MAX
  return raw
}

function coerceMode(raw: unknown): Mode {
  return VALID_MODES.has(raw as Mode) ? (raw as Mode) : DEFAULTS.mode
}
function coerceLayoutMode(raw: unknown): LayoutMode {
  return VALID_LAYOUT_MODES.has(raw as LayoutMode) ? (raw as LayoutMode) : DEFAULTS.layoutMode
}

export function coerceShape(raw: unknown): SettingsShape {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS }
  const src = raw as Record<string, unknown>
  return {
    mode: coerceMode(src['mode']),
    previewZoom: clampZoom(src['previewZoom']),
    layoutMode: coerceLayoutMode(src['layoutMode']),
  }
}

export function loadInitial(): SettingsShape {
  const raw = safeGetItem(STORAGE_KEY)
  if (raw === null) return { ...DEFAULTS }
  try {
    return coerceShape(JSON.parse(raw))
  } catch {
    return { ...DEFAULTS }
  }
}

// ----------------------------------------------------------------------------
// Reactive singleton — import and mutate; effects re-run automatically.
// ----------------------------------------------------------------------------

export const settings = $state<SettingsShape>(loadInitial())

let persistTimer: number | undefined
$effect.root(() => {
  $effect(() => {
    const snap: SettingsShape = {
      mode: settings.mode,
      previewZoom: settings.previewZoom,
      layoutMode: settings.layoutMode,
    }
    if (persistTimer !== undefined) clearTimeout(persistTimer)
    persistTimer = window.setTimeout(() => {
      safeSetItem(STORAGE_KEY, JSON.stringify(snap))
    }, 100)
  })
})
